// "Forgot your password?" page (kanban c369e94b). Public route. Always shows
// the same answer so it cannot be used to discover which emails have logins.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, Input } from '../components/common';
import { passwordResetService } from '../services/passwordReset';
import styles from './SetPasswordPage.module.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    const address = email.trim().toLowerCase();
    if (!address) {
      setFormError('Enter the email address you sign in with.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await passwordResetService.request(address);
      setSentMessage(res.message);
    } catch {
      setFormError('We could not send the link right now. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.box}>
        <div className={styles.logoSection}>
          <img src="/chemmasters-logo.png" alt="ChemMasters" className={styles.logo} />
        </div>

        <Card padding="lg">
          <CardBody>
            {sentMessage ? (
              <div className={styles.form}>
                <h2 className={styles.title}>Check your email</h2>
                <div className={styles.success} role="status">
                  {sentMessage}
                </div>
                <p className={styles.muted}>
                  Nothing arrived after a few minutes? Check your spam folder, or contact your administrator.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.form} data-section="forgot_password_form">
                <h2 className={styles.title}>Forgot your password?</h2>
                <p className={styles.lead}>
                  Enter your sign-in email and we will send you a link to choose a new one.
                </p>

                {formError && (
                  <div className={styles.error} role="alert">
                    {formError}
                  </div>
                )}

                <Input
                  label="Email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={submitting}
                  data-action="forgot_password_submit"
                >
                  Email me a link
                </Button>
              </form>
            )}
          </CardBody>
        </Card>

        <p className={styles.helpText}>
          <Link to="/login" data-action="forgot_password_back_to_sign_in">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
